import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
// NOTE: Removed motion/AnimatePresence to fix React DOM insertBefore errors globally
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { 
  Home, User, Utensils, Heart, Lightbulb, Users, ShoppingBag, 
  BookOpen, CreditCard, LogOut, ShoppingCart, Menu, X, Tag,
  ChevronDown, Apple, Activity, GraduationCap, Server, Shield
} from 'lucide-react';

const Navbar = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownRef = useRef(null);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setOpenDropdown(null);
  }, [location.pathname]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  // Grouped navigation structure
  const navGroups = [
    {
      id: 'main',
      items: [
        { path: '/dashboard', label: 'Acasă', icon: Home },
        { path: '/profile', label: 'Profil', icon: User },
      ]
    },
    {
      id: 'nutrition',
      label: 'Nutriție',
      icon: Apple,
      items: [
        { path: '/meal-plans', label: 'Planuri Mese', icon: Utensils },
        { path: '/shopping-list', label: 'Cumpărături', icon: ShoppingCart },
        { path: '/supermarkets', label: 'Oferte', icon: Tag },
        { path: '/recommendations', label: 'Recomandări AI', icon: Lightbulb },
      ]
    },
    {
      id: 'health',
      label: 'Sănătate',
      icon: Activity,
      items: [
        { path: '/health', label: 'Monitorizare', icon: Heart },
      ]
    },
    {
      id: 'community',
      label: 'Comunitate',
      icon: Users,
      items: [
        { path: '/social', label: 'Social', icon: Users },
        { path: '/producers', label: 'Producători', icon: ShoppingBag },
        { path: '/education', label: 'Educație', icon: BookOpen },
      ]
    },
    {
      id: 'account',
      items: [
        { path: '/subscription', label: 'Abonament', icon: CreditCard },
      ]
    },
    {
      id: 'admin',
      label: 'Admin',
      icon: Shield,
      adminOnly: true,
      items: [
        { path: '/api/docs', label: 'API Docs', icon: Server, external: true },
        { path: '/nutritionist', label: 'Panel Nutriționist', icon: GraduationCap },
        { path: '/admin/recipes', label: 'Rețete DB', icon: Utensils },
      ]
    }
  ];

  // Filter groups based on user role
  const isAdmin = user?.role === 'admin' || user?.role === 'nutritionist';
  const filteredNavGroups = navGroups.filter(group => !group.adminOnly || isAdmin);

  // Flatten for mobile
  const allNavItems = filteredNavGroups.flatMap(group => group.items);

  const isPathActive = (path) => location.pathname === path;
  const isGroupActive = (items) => items.some(item => location.pathname === item.path);

  // Get backend URL for API docs
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  return (
    <>
      <nav className="glass-morphism sticky top-0 z-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-8 h-8 sm:w-9 sm:h-9 bg-[#1A4D2E] rounded-full flex items-center justify-center shadow-md">
                <Heart className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <span className="text-lg sm:text-xl font-serif font-bold text-[#1A4D2E]">NutriHealth</span>
            </Link>

            {user && (
              <>
                {/* Desktop Navigation - Grouped */}
                <div className="hidden lg:flex items-center space-x-1" ref={dropdownRef}>
                  {filteredNavGroups.map((group) => {
                    // Single items without dropdown
                    if (!group.label) {
                      return group.items.map((item) => {
                        const Icon = item.icon;
                        const isActive = isPathActive(item.path);
                        
                        // External link (like API docs)
                        if (item.external) {
                          return (
                            <a
                              key={item.path}
                              href={`${BACKEND_URL}${item.path}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                              className="flex items-center space-x-1.5 px-3 py-2 rounded-full transition-all text-sm text-[#4a5568] hover:bg-[#F5F5F0]"
                            >
                              <Icon className="w-4 h-4" />
                              <span className="font-medium">{item.label}</span>
                            </a>
                          );
                        }
                        
                        return (
                          <Link
                            key={item.path}
                            to={item.path}
                            data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                            className={`flex items-center space-x-1.5 px-3 py-2 rounded-full transition-all text-sm ${
                              isActive
                                ? 'bg-[#1A4D2E] text-white shadow-md'
                                : 'text-[#4a5568] hover:bg-[#F5F5F0]'
                            }`}
                          >
                            <Icon className="w-4 h-4" />
                            <span className="font-medium">{item.label}</span>
                          </Link>
                        );
                      });
                    }

                    // Dropdown groups
                    const GroupIcon = group.icon;
                    const isActive = isGroupActive(group.items);
                    const isOpen = openDropdown === group.id;

                    return (
                      <div key={group.id} className="relative">
                        <button
                          onClick={() => setOpenDropdown(isOpen ? null : group.id)}
                          data-testid={`nav-group-${group.id}`}
                          className={`flex items-center space-x-1.5 px-3 py-2 rounded-full transition-all text-sm ${
                            isActive || isOpen
                              ? 'bg-[#1A4D2E] text-white shadow-md'
                              : 'text-[#4a5568] hover:bg-[#F5F5F0]'
                          }`}
                        >
                          <GroupIcon className="w-4 h-4" />
                          <span className="font-medium">{group.label}</span>
                          <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                        </button>

                        {/* Dropdown menu - CSS transitions instead of AnimatePresence */}
                        {isOpen && (
                          <div
                            className="absolute top-full left-0 mt-2 bg-white rounded-xl shadow-lg border border-gray-100 py-2 min-w-[180px] z-50 animate-in fade-in slide-in-from-top-2 duration-150"
                          >
                            {group.items.map((item) => {
                              const Icon = item.icon;
                              const itemActive = isPathActive(item.path);
                              
                              // External link
                              if (item.external) {
                                return (
                                  <a
                                    key={item.path}
                                    href={`${BACKEND_URL}${item.path}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                                    className="flex items-center space-x-2 px-4 py-2.5 transition-all text-[#4a5568] hover:bg-[#F5F5F0]"
                                  >
                                    <Icon className="w-4 h-4" />
                                    <span>{item.label}</span>
                                    <span className="text-xs text-gray-400">↗</span>
                                  </a>
                                );
                              }
                              
                              return (
                                <Link
                                  key={item.path}
                                  to={item.path}
                                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                                  className={`flex items-center space-x-2 px-4 py-2.5 transition-all ${
                                    itemActive
                                      ? 'bg-[#1A4D2E]/10 text-[#1A4D2E] font-medium'
                                      : 'text-[#4a5568] hover:bg-[#F5F5F0]'
                                  }`}
                                >
                                  <Icon className="w-4 h-4" />
                                  <span>{item.label}</span>
                                </Link>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Mobile Menu Button */}
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="lg:hidden p-2.5 text-[#1A4D2E] hover:bg-[#F5F5F0] rounded-xl transition-colors"
                  aria-label="Toggle menu"
                  data-testid="mobile-menu-toggle"
                >
                  {mobileMenuOpen ? (
                    <X className="w-6 h-6" />
                  ) : (
                    <Menu className="w-6 h-6" />
                  )}
                </button>
              </>
            )}

            {/* Desktop User Actions */}
            <div className="hidden lg:flex items-center space-x-3">
              {user ? (
                <>
                  <span className="text-sm text-[#4a5568] max-w-[150px] truncate">
                    {user.email || user.phone || user.name || 'Utilizator'}
                  </span>
                  <Button
                    onClick={logout}
                    variant="outline"
                    size="sm"
                    data-testid="logout-button"
                    className="border-[#1A4D2E]/20 text-[#1A4D2E] hover:bg-[#1A4D2E] hover:text-white rounded-full px-4"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Ieșire
                  </Button>
                </>
              ) : (
                <Link to="/auth">
                  <Button
                    data-testid="login-button"
                    className="bg-[#1A4D2E] text-white rounded-full hover:bg-[#143d24]"
                  >
                    Autentificare
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay - Using CSS transitions instead of AnimatePresence */}
      {mobileMenuOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 z-40 lg:hidden animate-in fade-in duration-200"
            onClick={() => setMobileMenuOpen(false)}
          />

          {/* Slide-in Menu */}
          <div
            className="fixed top-0 right-0 h-full w-[280px] sm:w-[320px] bg-white z-50 lg:hidden shadow-2xl animate-in slide-in-from-right duration-300"
          >
            {/* Mobile Menu Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-[#1A4D2E] rounded-full flex items-center justify-center">
                  <Heart className="w-4 h-4 text-white" />
                </div>
                <span className="font-serif font-bold text-[#1A4D2E]">NutriHealth</span>
              </div>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="p-2 text-[#4a5568] hover:bg-[#F5F5F0] rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Mobile Menu Content */}
            <div className="p-4 space-y-1 overflow-y-auto max-h-[calc(100vh-180px)]">
              {filteredNavGroups.map((group, groupIndex) => (
                <div key={group.id}>
                  {group.label && (
                    <p className="text-xs font-semibold text-[#4a5568] uppercase tracking-wider px-3 py-2 mt-3">
                      {group.label}
                    </p>
                  )}
                  {group.items.map((item, index) => {
                    const Icon = item.icon;
                    const isActive = isPathActive(item.path);
                    
                    // External link
                    if (item.external) {
                      return (
                        <div
                          key={item.path}
                          className="animate-in fade-in slide-in-from-right duration-200"
                          style={{ animationDelay: `${(groupIndex * group.items.length + index) * 30}ms` }}
                        >
                          <a
                            href={`${BACKEND_URL}${item.path}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                            className="flex items-center space-x-3 px-4 py-3 rounded-xl transition-all text-[#4a5568] hover:bg-[#F5F5F0]"
                          >
                            <Icon className="w-5 h-5" />
                            <span className="font-medium">{item.label}</span>
                            <span className="text-xs text-gray-400 ml-auto">↗</span>
                          </a>
                        </div>
                      );
                    }
                    
                    return (
                      <div
                        key={item.path}
                        className="animate-in fade-in slide-in-from-right duration-200"
                        style={{ animationDelay: `${(groupIndex * group.items.length + index) * 30}ms` }}
                      >
                        <Link
                          to={item.path}
                          data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                          className={`flex items-center space-x-3 px-4 py-3 rounded-xl transition-all ${
                            isActive
                              ? 'bg-[#1A4D2E] text-white shadow-md'
                              : 'text-[#4a5568] hover:bg-[#F5F5F0]'
                          }`}
                        >
                          <Icon className="w-5 h-5" />
                          <span className="font-medium">{item.label}</span>
                        </Link>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>

            {/* Mobile Menu Footer */}
            <div className="absolute bottom-0 left-0 right-0 p-4 border-t bg-white">
              {user && (
                <div className="space-y-3">
                  <div className="flex items-center space-x-3 px-3">
                    <div className="w-10 h-10 bg-[#1A4D2E]/10 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-[#1A4D2E]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#1A4D2E] truncate">
                        {user.name || user.email?.split('@')[0] || 'Utilizator'}
                      </p>
                      <p className="text-xs text-[#4a5568] truncate">
                        {user.email || user.phone}
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={logout}
                    variant="outline"
                    data-testid="mobile-logout-button"
                    className="w-full border-red-200 text-red-600 hover:bg-red-50 rounded-xl"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Deconectare
                  </Button>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Navbar;
